import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    signal,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { filter, Subject, Subscription, switchMap, tap } from 'rxjs';
import { ImgWithSelection } from 'src/app/img-db/model/img-with-selection';
import { ImgAnnotation } from '../model/img-annotation';
import { ImgCategory } from '../model/img-category';
import { ImgDbZoomService } from '../services/img-db-zoom.service';
import { AnnotationStateService } from '../services/annotation-state.service';
import { ColorService } from '../../shared/services/color.service';
import { ImagesStorageService } from '../services/images-storage.service';
import { take, takeUntil } from 'rxjs/operators';
import { PagingResult } from '../../shared/models/pageable';
import { AuthenticationService } from '../../authentication/services/authentication.service';
import { SplitScreenState } from '../../project-split-screen/components/project-split-screen/project-split-screen.component';
import { KeyEventsService } from '../services/key-events.service';
import { isTextEntryTarget } from '../is-text-entry-target';
import { AnnotationClipboardData } from '../model/annotation-clipboard-data';
import { ToastMessageService } from '../../shared/services/toast-message.service';
import {
    CanvasBox,
    CanvasBoxChange,
    ImageCanvasViewportComponent,
    toCanvasBox,
} from '../../shared/image-canvas/image-canvas-viewport.component';
import { ImageRect } from '../../shared/image-canvas/image-canvas-viewport';

/**
 * Canvas labeling viewer. Draws and edits annotations on the shared image canvas.
 */
@Component({
    selector: 'app-img-labeling-canvas-viewer',
    templateUrl: './img-labeling-canvas-viewer.component.html',
    styleUrls: ['./img-labeling-canvas-viewer.component.scss'],
    standalone: false,
})
export class ImgLabelingCanvasViewerComponent
    implements OnInit, OnChanges, OnDestroy
{
    @Output()
    zoomLevelChanged: EventEmitter<number> = new EventEmitter();

    @Input() selectedCategory: ImgCategory;
    @Input()
    set categories(categories: ImgCategory[]) {
        this.currentCategories = categories;
        this.rebuildCanvasBoxes();
    }
    get categories(): ImgCategory[] {
        return this.currentCategories;
    }
    @Input() splitScreenState: SplitScreenState;
    @Input() canEdit: boolean;
    isDraggingActive: boolean = false;

    selectedImage: ImgWithSelection;
    imageLoaded: boolean = false;
    annotationsLoading: boolean = false;
    /**
     * Boxes bound to the viewport. Rebuilt only when annotations or
     * categories change, so the viewport gets a stable array between changes.
     */
    labelingCanvasBoxes: CanvasBox[] = [];
    private currentAnnotations: ImgAnnotation[];
    private currentCategories: ImgCategory[];

    canvasLevel = 0;
    private canvasOriginalAnnotation: ImgAnnotation | null = null;
    @ViewChild(ImageCanvasViewportComponent)
    canvasViewport?: ImageCanvasViewportComponent;

    zoomConfigPanOnClickDrag = signal<boolean>(false);

    // used to set and clear saving Interval
    saveIntervalId: ReturnType<typeof setInterval>;

    private subscriptions: Subscription[] = [];
    private destroy$: Subject<void> = new Subject<void>();

    showImageContainer: boolean = false;

    isPanButtonActive: boolean = false;
    isPanButtonClicked: boolean = this.isPanButtonActive;
    isRulerButtonActive: boolean = false;
    showSizeLabels: boolean = this.isRulerButtonActive;
    keydownBAccepted: boolean = false;
    private modalOpen = false;

    featureButtonActiveIdx: number | null = null;

    readonly TOAST_MESSAGE_DELAY = { delay: 2000 };

    constructor(
        public annotationStateService: AnnotationStateService,
        private colorService: ColorService,
        private zoomService: ImgDbZoomService,
        private storage: ImagesStorageService,
        private authService: AuthenticationService,
        private keyEventsService: KeyEventsService,
        private messageService: ToastMessageService
    ) {}

    get currentZoomLevel() {
        return this.zoomService.getCurrentZoomLevel(1);
    }

    get labelCanvasMaxLevel(): number {
        return this.zoomService.getCurrentMax(1);
    }

    get annotations(): ImgAnnotation[] {
        return this.currentAnnotations;
    }

    set annotations(annotations: ImgAnnotation[]) {
        this.currentAnnotations = annotations;
        this.rebuildCanvasBoxes();
    }

    private rebuildCanvasBoxes(): void {
        this.labelingCanvasBoxes = (this.currentAnnotations ?? []).map(
            (annotation) => {
                const categoryName = this.getImgCategoryName(
                    annotation.categoryId
                );
                return toCanvasBox(annotation, {
                    color: this.getColor(categoryName),
                    label: categoryName,
                });
            }
        );
    }

    get isImageAndAnnotationsReady(): boolean {
        return this.imageLoaded && !this.annotationsLoading;
    }

    ngOnInit() {
        this.subscriptions.push(
            this.annotationStateService.$loading.subscribe(
                (isLoading: boolean) => {
                    this.annotationsLoading = isLoading;
                }
            )
        );

        this.subscriptions.push(
            this.storage.imagesPage$
                .pipe(
                    filter(
                        (page: PagingResult<ImgWithSelection>) => page !== null
                    ),
                    take(1),
                    switchMap(() =>
                        this.annotationStateService.$annotations.pipe(
                            tap((annotations: ImgAnnotation[]) => {
                                this.annotations = annotations;
                            }),
                            takeUntil(this.destroy$)
                        )
                    ),
                    switchMap(() =>
                        this.zoomService.zoomLevelLabelDialog.pipe(
                            tap((zoomLevel: number) => {
                                this.onZoomChanged(zoomLevel);
                            }),
                            takeUntil(this.destroy$)
                        )
                    )
                )
                .subscribe()
        );
        this.subscriptions.push(
            this.authService.logoutTriggered$.subscribe(() => {
                this.annotationStateService
                    .saveCommandsBeforeLogout()
                    .subscribe(() => {
                        this.authService.logoutPreparationsFinished$.next(true);
                    });
            })
        );
        this.subscriptions.push(
            this.storage.selectedImage$.subscribe((img: ImgWithSelection) => {
                if (img) {
                    if (
                        this.selectedImage?.processedUrl &&
                        this.selectedImage.processedUrl !== img.processedUrl
                    ) {
                        this.imageLoaded = false;
                    }
                    clearInterval(this.saveIntervalId);
                    this.selectedImage = img;
                    this.showImageContainer = true;
                    this.saveIntervalId = setInterval(() => {
                        this.annotationStateService
                            .saveCommands(true, img.id)
                            .subscribe();
                    }, 20000);
                }
            })
        );

        this.subscriptions.push(
            this.storage.pageChanged$.subscribe(
                () => (this.showImageContainer = false)
            )
        );

        this.subscriptions.push(
            this.keyEventsService.$modalOpen.subscribe(
                (isOpen: boolean) => (this.modalOpen = isOpen)
            )
        );
    }

    /**
     * Single entry point for keyboard shortcuts. Space panning works even
     * with a modal open; everything else pauses while one is open, and
     * editing shortcuts additionally need edit permission.
     */
    @HostListener('document:keydown', ['$event'])
    onDocumentKeydown(event: KeyboardEvent): void {
        if (isTextEntryTarget(event.target)) {
            return;
        }
        if (this.modalOpen) {
            return;
        }
        this.onActivateSpaceDragging(event);
        void this.handleCtrlCEvent(event);
        this.handleSEvent(event);
        if (this.canEdit) {
            void this.handleCtrlVEvent(event);
            this.handleKeydownBEvent(event);
            this.handleKeydownUndoRedoEvents(event);
        }
    }

    @HostListener('document:keyup', ['$event'])
    onDocumentKeyup(event: KeyboardEvent): void {
        this.onDeactivateSpaceDragging(event);
        if (!this.modalOpen && this.canEdit) {
            this.handleKeyupBEvent(event);
        }
    }

    private handleSEvent(event: KeyboardEvent) {
        if (event.key === 's' || event.key === 'S') {
            this.onRulerButtonClick();
        }
    }

    private async handleCtrlVEvent(event: KeyboardEvent) {
        if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === 'v' || event.key === 'V')
        ) {
            event.stopPropagation();

            const annotationClipboardData: AnnotationClipboardData =
                await this.keyEventsService.readAnnotationFromClipboard();
            if (!annotationClipboardData) {
                this.messageService.showTranslatedWarningMsg(
                    'imgDb.imgLabeling.pasteAnnotationEvent.invalidAnnotation',
                    null,
                    this.TOAST_MESSAGE_DELAY
                );
                return;
            }

            const categoryOfAnnotationFromClipboard = this.categories.find(
                (category) =>
                    category.name === annotationClipboardData.categoryName
            );

            if (!categoryOfAnnotationFromClipboard) {
                this.messageService.showTranslatedErrorMsg(
                    'imgDb.imgLabeling.pasteAnnotationEvent.notExistingCategory',
                    {
                        categoryName: annotationClipboardData.categoryName,
                    },
                    this.TOAST_MESSAGE_DELAY
                );
                return;
            }

            const centeredRect = this.centerAnnotation(
                annotationClipboardData.bbox.width,
                annotationClipboardData.bbox.height
            );

            if (!centeredRect) {
                return;
            }

            const annotation = new ImgAnnotation();
            annotation.imageId = this.selectedImage.id;
            annotation.categoryId = categoryOfAnnotationFromClipboard.id;
            annotation.bbox = centeredRect;

            this.annotationStateService.addAnnotation(annotation);

            this.messageService.showTranslatedSuccessMsg(
                'imgDb.imgLabeling.pasteAnnotationEvent.success',
                {
                    name: categoryOfAnnotationFromClipboard.name,
                },
                this.TOAST_MESSAGE_DELAY
            );
        }
    }

    private centerAnnotation(
        annotationWidth: number,
        annotationHeight: number
    ): DOMRect | null {
        const imgWidth = this.selectedImage.width;
        const imgHeight = this.selectedImage.height;

        if (annotationWidth <= 0 || annotationHeight <= 0) {
            this.messageService.showTranslatedErrorMsg(
                'imgDb.imgLabeling.pasteAnnotationEvent.nonPositiveDimensions',
                null,
                this.TOAST_MESSAGE_DELAY
            );
            return null;
        }

        if (annotationWidth > imgWidth || annotationHeight > imgHeight) {
            this.messageService.showTranslatedErrorMsg(
                'imgDb.imgLabeling.pasteAnnotationEvent.tooLarge',
                null,
                this.TOAST_MESSAGE_DELAY
            );
            return null;
        }

        const visible = this.canvasViewport?.getVisibleImageRect();
        if (!visible) {
            return null;
        }
        const centeredX = this.clamp(
            visible.x + visible.width / 2 - annotationWidth / 2,
            0,
            imgWidth - annotationWidth
        );
        const centeredY = this.clamp(
            visible.y + visible.height / 2 - annotationHeight / 2,
            0,
            imgHeight - annotationHeight
        );
        return this.offsetBoundingBoxIfOverlapping(
            new DOMRect(centeredX, centeredY, annotationWidth, annotationHeight)
        );
    }

    private clamp(value: number, min: number, max: number): number {
        return Math.min(max, Math.max(min, value));
    }

    private offsetBoundingBoxIfOverlapping(rect: DOMRect): DOMRect {
        const hasSameStartPoint = (this.annotations ?? []).some(
            (annotation: ImgAnnotation) =>
                annotation.bbox.x === rect.x && annotation.bbox.y === rect.y
        );

        if (hasSameStartPoint) {
            const newRect = new DOMRect(
                rect.x + 10,
                rect.y + 10,
                rect.width,
                rect.height
            );

            if (
                this.selectedImage.width >= newRect.x + newRect.width &&
                this.selectedImage.height >= newRect.y + newRect.height
            ) {
                return this.offsetBoundingBoxIfOverlapping(newRect);
            }
        }
        return rect;
    }

    private async handleCtrlCEvent(event: KeyboardEvent) {
        if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === 'c' || event.key === 'C')
        ) {
            const selectedAnnotation =
                this.annotationStateService.selectedAnnotation;

            if (!selectedAnnotation) {
                this.messageService.showTranslatedWarningMsg(
                    'imgDb.imgLabeling.copyAnnotationEvent.noSelectedAnnotation',
                    null,
                    this.TOAST_MESSAGE_DELAY
                );
                return;
            }

            const categoryOfSelectedAnnotation = this.categories.find(
                (category) => category.id === selectedAnnotation.categoryId
            );
            if (!categoryOfSelectedAnnotation) {
                return;
            }

            event.preventDefault();

            const annotationDataToCopy: AnnotationClipboardData = {
                categoryName: categoryOfSelectedAnnotation.name,
                bbox: {
                    width: selectedAnnotation.bbox.width,
                    height: selectedAnnotation.bbox.height,
                },
            };

            await this.keyEventsService.copyAnnotationToClipboard(
                annotationDataToCopy
            );
            this.messageService.showTranslatedSuccessMsg(
                'imgDb.imgLabeling.copyAnnotationEvent.success',
                { name: annotationDataToCopy.categoryName },
                this.TOAST_MESSAGE_DELAY
            );
        }
    }

    private handleKeydownBEvent(event: KeyboardEvent) {
        if (event.key === 'b' || event.key === 'B') {
            event.preventDefault();
            if (
                !this.keydownBAccepted &&
                this.isPanButtonActive &&
                this.isEditable()
            ) {
                this.keydownBAccepted = true;
                this.toggleImageDragging(false);
            }
        }
    }

    private handleKeyupBEvent(event: KeyboardEvent) {
        if (event.key === 'b' || event.key === 'B') {
            event.preventDefault();
            if (this.keydownBAccepted) {
                this.keydownBAccepted = false;
                if (this.isPanButtonClicked) {
                    this.toggleImageDragging(true);
                }
            }
        }
    }

    private handleKeydownUndoRedoEvents(event: KeyboardEvent) {
        if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === 'z' || event.key === 'Z')
        ) {
            event.preventDefault();
            if (this.annotationStateService.hasUndo() && this.canEdit) {
                this.annotationStateService.undo();
            }
        } else if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === 'y' || event.key === 'Y')
        ) {
            event.preventDefault();
            if (this.annotationStateService.hasRedo() && this.canEdit) {
                this.annotationStateService.redo();
            }
        }
    }

    ngOnDestroy(): void {
        this.annotationStateService
            .saveCommands(true, this.selectedImage?.id)
            .subscribe();
        this.destroy$.next();
        this.destroy$.complete();
        this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
        this.annotationStateService.$annotations.next([]);
        clearInterval(this.saveIntervalId);
        this.annotationStateService.unselectImg();
    }

    private onZoomChanged(
        zoomLevel: number,
        updateGlobalZoom: boolean = false
    ) {
        if (
            zoomLevel > this.zoomService.getCurrentMax(1) ||
            zoomLevel < ImgDbZoomService.MIN_IMAGE_ZOOM ||
            !this.selectedImage
        ) {
            return;
        }
        this.canvasLevel = zoomLevel;
        if (
            updateGlobalZoom &&
            this.zoomService.currentZoomLevelLabelDialog !== zoomLevel
        ) {
            this.zoomService.zoomLevelLabelDialog.next(zoomLevel);
        }
    }

    onPanButtonClick() {
        this.isPanButtonActive = !this.isPanButtonActive;
        this.isPanButtonClicked = this.isPanButtonActive;
        this.toggleImageDragging(this.isPanButtonActive);
    }

    onRulerButtonClick() {
        this.isRulerButtonActive = !this.isRulerButtonActive;
        this.showSizeLabels = this.isRulerButtonActive;
    }

    onActivateSpaceDragging(event: KeyboardEvent) {
        if (event.key === ' ' && this.canvasLevel > 0) {
            event.preventDefault();
            this.toggleImageDragging(true);
        }
    }

    onDeactivateSpaceDragging(event: KeyboardEvent) {
        if (event.key === ' ' && !this.isPanButtonClicked) {
            this.toggleImageDragging(false);
        }
    }

    // Disables and enables dragging the image
    toggleImageDragging(enable: boolean) {
        this.zoomConfigPanOnClickDrag.set(enable);
        this.isDraggingActive = enable;
        this.isPanButtonActive = enable;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedCategory) {
            this.toggleImageDragging(false);
            this.isPanButtonClicked = false;
        } else if (changes.splitScreenState && this.imageLoaded) {
            this.canvasViewport?.refit();
        }
    }

    getImgCategoryName(id: string) {
        return this.categories?.find(
            (category: ImgCategory) => category.id === id
        )?.name;
    }

    onCanvasLevelChange(level: number): void {
        this.canvasLevel = level;
        this.zoomService.setCurrentZoomLevel(level);
        this.zoomLevelChanged.emit(level);
    }

    onCanvasSelect(id: string | null): void {
        // selectAnnotation toggles, so re-selecting the active box clears it
        const targetId =
            id ?? this.annotationStateService.selectedAnnotation?.id;
        if (targetId) {
            this.annotationStateService.selectAnnotation(targetId);
        }
    }

    onCanvasDrawEnd(bbox: ImageRect): void {
        if (
            bbox.width <= 0 ||
            bbox.height <= 0 ||
            !this.selectedCategory ||
            !this.selectedImage
        ) {
            return;
        }
        const annotation = new ImgAnnotation();
        annotation.imageId = this.selectedImage.id;
        annotation.categoryId = this.selectedCategory.id + '';
        annotation.bbox = new DOMRect(bbox.x, bbox.y, bbox.width, bbox.height);
        this.annotationStateService.addAnnotation(annotation);
        // addAnnotation assigns the id and stores an inactive copy. Selecting
        // afterwards highlights that box; calling it first would miss the id.
        this.annotationStateService.selectAnnotation(annotation.id);
    }

    onCanvasModifyStart(): void {
        const selected = this.annotationStateService.selectedAnnotation;
        if (!selected) {
            return;
        }
        this.annotationStateService.bboxCurrentlyModifiable = true;
        this.canvasOriginalAnnotation =
            this.annotationStateService.deepCopyImgAnnotation(selected);
    }

    onCanvasModifyEnd(change: CanvasBoxChange): void {
        this.annotationStateService.bboxCurrentlyModifiable = false;
        const selected = this.annotationStateService.selectedAnnotation;
        const original = this.canvasOriginalAnnotation;
        this.canvasOriginalAnnotation = null;
        if (!selected || !original || selected.id !== change.id) {
            return;
        }
        selected.bbox = new DOMRect(
            change.bbox.x,
            change.bbox.y,
            change.bbox.width,
            change.bbox.height
        );
        this.annotationStateService.updateAnnotation(selected, original);
    }

    onCanvasModifyCancel(): void {
        this.annotationStateService.bboxCurrentlyModifiable = false;
        this.canvasOriginalAnnotation = null;
    }

    onCanvasImageLoaded(): void {
        this.annotationStateService.selectedAnnotation = null;
        this.imageLoaded = true;
        this.canvasViewport?.refit();
        this.onZoomChanged(ImgDbZoomService.MIN_IMAGE_ZOOM, true);
    }

    isEditable(): boolean {
        return (
            this.selectedCategory !== null &&
            this.selectedCategory !== undefined &&
            this.canEdit
        );
    }

    getColor(categoryName: string) {
        return this.colorService.getColor(categoryName);
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event: BeforeUnloadEvent) {
        if (
            this.annotationStateService?.changedAnnotationState() &&
            this.selectedImage?.id
        ) {
            this.annotationStateService
                .saveCommands(false, this.selectedImage.id)
                .subscribe();
            event.preventDefault();
            event.returnValue = false;
        }
    }
}
